import { useState, useEffect } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { TRIAL_DAYS } from '@mirai/shared';
import { api } from '../api/public';
import s from './Pricing.module.css';

interface Props {
  onTrialClick: () => void;
  onLeadClick: (planId: number) => void;
}

interface Plan {
  id: number;
  name: string;
  price_monthly: number;
  max_users: number;
  max_properties: number;
  modules: string[];
}

// モジュールIDから日本語ラベルへの変換マップ
const MODULE_LABELS: Record<string, string> = {
  properties: '物件管理',
  crm: '顧客CRM',
  contracts: '電子契約・書類生成',
  portals: 'ポータル一括入稿',
  rent_management: '家賃・入金管理',
  ai_agent: 'AIエージェント',
};

// DBと一致するフォールバック値
const FALLBACK_PLANS: Plan[] = [
  { id: 1, name: 'トライアル', price_monthly: 0, max_users: 2, max_properties: 10, modules: ['properties', 'crm', 'contracts'] },
  { id: 2, name: 'スタンダード', price_monthly: 9800, max_users: 5, max_properties: 100, modules: ['properties', 'crm', 'contracts', 'portals'] },
  { id: 3, name: 'プロフェッショナル', price_monthly: 29800, max_users: 20, max_properties: 500, modules: ['properties', 'crm', 'contracts', 'portals', 'rent_management'] },
  { id: 4, name: 'エンタープライズ', price_monthly: 98000, max_users: 999, max_properties: 9999, modules: ['properties', 'crm', 'contracts', 'portals', 'rent_management', 'ai_agent'] },
];

function formatPrice(price: number) {
  return price === 0 ? '¥0' : `¥${price.toLocaleString()}`;
}

function resolveModuleLabel(mod: string): string {
  return MODULE_LABELS[mod] || mod;
}

export function Pricing({ onTrialClick, onLeadClick }: Props) {
  const ref = useScrollReveal<HTMLElement>();
  const [plans, setPlans] = useState<Plan[]>(FALLBACK_PLANS);

  useEffect(() => {
    api.getPlans().then(setPlans).catch(() => {});
  }, []);

  const isTrial = (plan: Plan) => plan.price_monthly === 0;
  const isRecommended = (plan: Plan) => plan.name === 'プロフェッショナル' || plan.id === 3;

  return (
    <section id="pricing" className={`${s.section} reveal`} ref={ref}>
      <div className={s.inner}>
        <h2 className={s.title}>シンプルな料金プラン</h2>
        <p className={s.subtitle}>{`無料トライアルプランで${TRIAL_DAYS}日間お試しいただけます`}</p>
        <div className={s.grid}>
          {plans.map((plan) => (
            <div key={plan.id} className={`${s.card} ${isRecommended(plan) ? s.recommended : ''}`}>
              {isRecommended(plan) && <span className={s.badge}>おすすめ</span>}
              <div className={s.planName}>{plan.name}</div>
              <div className={s.price}>
                {formatPrice(plan.price_monthly)}
                {!isTrial(plan) && <span className={s.priceUnit}>/月</span>}
              </div>
              <div className={s.priceSub}>
                {isTrial(plan) ? `${TRIAL_DAYS}日間` : plan.id === 4 ? '年間契約で20%割引' : `${plan.max_users}ユーザーまで`}
              </div>
              <ul className={s.featureList}>
                <li className={s.featureItem}>
                  <span className={s.check}>&#10003;</span>
                  <span>物件 {plan.max_properties.toLocaleString()}件まで</span>
                </li>
                {plan.modules.map((mod) => (
                  <li key={mod} className={s.featureItem}>
                    <span className={s.check}>&#10003;</span>
                    <span>{resolveModuleLabel(mod)}</span>
                  </li>
                ))}
              </ul>
              {isTrial(plan) ? (
                <button className={s.btnPrimary} onClick={onTrialClick}>
                  トライアル開始
                </button>
              ) : (
                <button
                  className={isRecommended(plan) ? s.btnPrimary : s.btnSecondary}
                  onClick={() => onLeadClick(plan.id)}
                >
                  お申込み
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
